import { toast } from "sonner";

/**
 * Error & toast conventions for the control plane
 * ------------------------------------------------
 * Channels:
 * - Mutations → toast (per-site `onError`, or the global MutationCache handler in
 *   query.provider.tsx when a mutation defines none).
 * - Query/read failures and form validation → inline UI (error branches, banners,
 *   auth <Alert> surfaces). Background refetches must never toast.
 *
 * Copy style:
 * - Sentence case, trailing period, no exclamation marks, active voice.
 * - Success: "<Object> <past-tense verb>." - "Invite sent.", "Copied."
 * - Error fallback: "Couldn't <verb> <object>." - used only when the backend sent no
 *   message. Backend messages pass through verbatim (ResponseStatusException reasons
 *   are already human-written).
 * - Never "Successfully…", "Failed to…", "Error:", or raw HTTP jargon in user copy.
 *
 * fieldErrors: the backend's validation 400 sends flat "field: message" strings; they
 * render as toast description lines. Per-form mapping onto inputs is deferred - field
 * names are Java DTO properties with no guaranteed Zod-path correspondence, and client
 * Zod intercepts nearly all 400s. The data stays on ApiError for forms to adopt later.
 */

/**
 * Typed API error carrying the backend error body. `correlationId` is for console and
 * support tooling only - NEVER render it in user-facing UI (owner decision).
 */
export class ApiError extends Error {
  readonly status: number;
  /** Backend `error` (baseline shape) or `errorCode` (KRA/gateway shape). */
  readonly code?: string;
  /** Validation 400: flat "field: message" strings from the backend. */
  readonly fieldErrors?: string[];
  readonly correlationId?: string;

  constructor(args: {
    status: number;
    message: string;
    code?: string;
    fieldErrors?: string[];
    correlationId?: string;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.status = args.status;
    this.code = args.code;
    this.fieldErrors = args.fieldErrors;
    this.correlationId = args.correlationId;
  }
}

export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

/** Standard error toast: backend/Error message with fallback; fieldErrors as description lines. */
export function toastError(err: unknown, fallback: string): void {
  const description =
    err instanceof ApiError && err.fieldErrors?.length ? err.fieldErrors.join("\n") : undefined;
  toast.error(getErrorMessage(err, fallback), { description });
}
